import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wordmark } from '../../../components/Wordmark';
import { Button } from '@/components/ui/button';
import type { SessionUser } from '../../../hooks/useSession';

export interface NavProps {
  user: SessionUser | null;
}

/**
 * Sticky landing nav (spec §7.3.1.4 — Nav).
 *
 * Behavior:
 * - transparent at the very top
 * - blurred surface + bottom hairline once scrolled past 8px
 * - swaps the right-side CTA when authenticated
 */
export function Nav({ user }: NavProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
        className={
        'sticky top-0 z-30 transition-colors ' +
        (scrolled
          ? 'border-border/60 bg-background/80 backdrop-blur border-b'
          : 'border-transparent border-b bg-transparent')
      }
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
        <Wordmark to="/" />

        <div className="flex items-center gap-4 text-sm">
          {user ? (
            <Link to="/app" className="text-primary font-medium hover:underline">
              Open dashboard →
            </Link>
          ) : (
            <>
              <Link to="/sign-in" className="text-muted-foreground hover:text-foreground transition">
                Sign in
              </Link>
              <Button variant="secondary" size="sm" className="bg-foreground text-background hover:bg-foreground/90" asChild>
                <Link to="/sign-up">Try it free</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
